'use client'
import cn from 'classnames'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import type {
  Content,
  FeatureHighlightsSliceDefaultPrimaryItemsItem,
  FeatureHighlightsSliceWithBoxesPrimaryItemsItem,
  FeatureHighlightsSliceWithIconsPrimaryItemsItem,
} from '@/content-types'
import { asText, isFilled } from '@/lib/content-fields'
import { ContentLink, ContentNextImage, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { getBackgroundColor } from '@/utils/helpers'
import { linkResolver } from '@/utils/linkResolver'

/**
 * Props for `FeatureHighlights`.
 */
export type FeatureHighlightsProps = SliceComponentProps<Content.FeatureHighlightsSlice>

/**
 * Component for "FeatureHighlights" Slices.
 */
const FeatureHighlights: FC<FeatureHighlightsProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={
        isFilled.select(slice.primary.background) ? getBackgroundColor(slice.primary.background) : ''
      }
    >
      <div className="container flex flex-col items-center text-center">
        {isFilled.richText(slice.primary.title) && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
              duration: 0.6,
            }}
          >
            <RichText
              field={slice.primary.title}
              components={{
                heading2: ({ children }) => <h2 className="section-title">{children}</h2>,
                heading3: ({ children }) => <h3 className="section-title">{children}</h3>,
                heading5: ({ children }) => <h5 className="section-title">{children}</h5>,
              }}
            />
          </motion.div>
        )}

        {isFilled.richText(slice.primary.description) && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
              duration: 0.6,
              delay: 0.1,
            }}
            className="mt-3.5 max-w-[936px] text-center"
          >
            <RichText
              field={slice.primary.description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[19px] leading-7 text-[#4b4b4b] md:text-[21px] md:leading-8">
                    {children}
                  </p>
                ),
                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
              }}
            />
          </motion.div>
        )}

        {slice.primary.items && slice.primary.items.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
              duration: 0.6,
              delay: 0.2,
            }}
            className="mt-14 w-full"
          >
            {slice.variation === 'default' && slice.primary.carousel && (
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-5">
                  {slice.variation === 'default' &&
                    slice.primary.items &&
                    slice.primary.items.map((item) => (
                      <CarouselItem
                        key={asText(item.title)}
                        className="basis-full pl-2 sm:basis-1/2 md:pl-5 lg:basis-1/4"
                      >
                        <Default key={asText(item.title)} item={item} background={slice.primary.card_type} />
                      </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious className="hidden xl:inline-flex" />
                <CarouselNext className="hidden xl:inline-flex" />
              </Carousel>
            )}
            {/* If carousel is not enabled, show the items in a grid */}
            {slice.variation === 'default' && !slice.primary.carousel && (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {slice.primary.items?.map((item) => (
                  <Default key={asText(item.title)} item={item} background={slice.primary.card_type} />
                ))}
              </div>
            )}
            {slice.variation === 'withIcons' && (
              <div
                className={cn(
                  'grid grid-cols-1 gap-x-5 gap-y-10 md:grid-cols-2',
                  slice.primary.columncount && `lg:grid-cols-${slice.primary.columncount}`
                )}
              >
                {slice.primary.items?.map((item) => (
                  <WithIcon key={asText(item.title)} item={item} background={slice.primary.card_type} />
                ))}
              </div>
            )}

            {slice.variation === 'withBoxes' && (
              <div
                className={cn(
                  'grid grid-cols-1 gap-x-5 gap-y-10 md:grid-cols-2',
                  slice.primary.columncount && `lg:grid-cols-${slice.primary.columncount}`
                )}
              >
                {slice.primary.items?.map((item) => (
                  <WithBoxes key={asText(item.title)} item={item} />
                ))}
              </div>
            )}

            {slice.variation === 'default' && isFilled.link(slice.primary.button) && (
              <div className="mt-10 flex justify-center">
                <ContentLink
                  field={slice.primary.button}
                  linkResolver={linkResolver}
                  className="bg-primary hover:bg-primary-dark group items-center rounded-full border-1 px-7 py-3 text-center font-semibold text-nowrap text-white transition duration-200 ease-in-out md:text-xs xl:text-lg"
                >
                  {slice.primary.button.text}
                </ContentLink>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  )
}

const Default = ({
  item,
}: {
  item: FeatureHighlightsSliceDefaultPrimaryItemsItem
  background: 'Flat' | 'White background' | 'Gray background' | 'Border and background'
}) => (
  <div className="group/card relative aspect-[4/5] overflow-hidden rounded-[24px] bg-[#1c0826] text-left ring-1 ring-black/5 transition duration-300 hover:-translate-y-1">
    {item.image && (
      <ContentNextImage
        field={item.image}
        fallbackAlt=""
        fill
        sizes="(min-width: 768px) 25vw, 100vw"
        className="object-cover saturate-[0.9] contrast-[1.05] transition duration-700 group-hover/card:scale-[1.06]"
      />
    )}
    <div className="absolute inset-0 bg-[#4a1a63]/20 mix-blend-multiply" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#120616] via-[#120616]/40 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-5 text-white">
      <RichText
        field={item.title}
        components={{
          heading3: ({ children }) => <h3 className="font-rubik text-[22px] font-semibold">{children}</h3>,
          heading4: ({ children }) => <h4 className="font-rubik text-[22px] font-semibold">{children}</h4>,
          heading5: ({ children }) => <h5 className="font-rubik text-[22px] font-semibold">{children}</h5>,
          heading6: ({ children }) => <h6 className="font-rubik text-[22px] font-semibold">{children}</h6>,
        }}
      />
      {item.description && <p className="mt-1.5 text-[14px] leading-5 text-white/75">{item.description}</p>}
      <span className="text-signal mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold">
        {isFilled.link(item.link) ? item.link.text : 'Learn more'}
        <IconComponent
          icon="ChevronRight"
          size="xsmall"
          className="!size-3 transition group-hover/card:translate-x-0.5"
        />
      </span>
    </div>
    {isFilled.link(item.link) && (
      <ContentLink field={item.link} linkResolver={linkResolver} className="absolute inset-0 z-10">
        <span className="sr-only">{item.link.text}</span>
      </ContentLink>
    )}
  </div>
)

const WithIcon = ({
  item,
  background,
}: {
  item: FeatureHighlightsSliceWithIconsPrimaryItemsItem
  background: 'Flat' | 'White background' | 'Gray background' | 'Border and background'
}) => (
  <div
    className={cn(
      'group/card relative flex flex-col items-center rounded-2xl px-6 py-9 text-center ring-1 ring-black/5 transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-20px_rgba(129,12,168,0.45)]',
      background === 'Flat' && 'bg-white shadow-[0_1px_2px_rgba(45,3,59,0.06)]',
      background === 'White background' && 'bg-white shadow-[0_1px_2px_rgba(45,3,59,0.06)]',
      background === 'Gray background' && 'bg-light-gray',
      background === 'Border and background' &&
        'bg-gradient-to-b from-[rgb(129,12,168)] from-[0%] to-[rgb(45,3,59)] to-[100%]'
    )}
  >
    {isFilled.select(item.icon) && (
      <div className="bg-primary/8 text-primary group-hover/card:bg-primary mb-5 flex size-16 items-center justify-center rounded-2xl transition duration-300 group-hover/card:text-white">
        <IconComponent icon={item.icon} size="medium" className="!size-8" />
      </div>
    )}

    <RichText
      field={item.title}
      components={{
        heading3: ({ children }) => (
          <h4 className="text-foreground text-[21px] font-semibold tracking-tight">{children}</h4>
        ),
        heading4: ({ children }) => (
          <h4 className="text-foreground text-[21px] font-semibold tracking-tight">{children}</h4>
        ),
        heading5: ({ children }) => (
          <h5 className="text-foreground text-[21px] font-semibold tracking-tight">{children}</h5>
        ),
        heading6: ({ children }) => (
          <h6 className="text-foreground text-[21px] font-semibold tracking-tight">{children}</h6>
        ),
      }}
    />

    <div className="mt-2 flex w-full flex-col items-center justify-start gap-2">
      <RichText
        field={item.text}
        components={{
          paragraph: ({ children }) => <p className="text-[16px] leading-6 text-[#4b4b4b]">{children}</p>,
        }}
      />
    </div>

    {isFilled.link(item.link) && item.link.variant === 'Overlay' ? (
      <ContentLink field={item.link} linkResolver={linkResolver} className="absolute inset-0 z-10" />
    ) : (
      <div className="">
        {/* TODO: Implement other link variants */}
        <ContentLink
          field={item.link}
          linkResolver={linkResolver}
          className="hover:text-primary text-base font-semibold"
        />
      </div>
    )}
  </div>
)

const WithBoxes = ({ item }: { item: FeatureHighlightsSliceWithBoxesPrimaryItemsItem }) => (
  <div className="relative flex flex-col items-start rounded-2xl bg-white px-6 py-9 text-left shadow-[0_1px_2px_rgba(45,3,59,0.06)] ring-1 ring-black/5 transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-20px_rgba(129,12,168,0.45)]">
    {isFilled.select(item.icon) ? (
      <div className="flex flex-row items-start gap-x-4">
        {isFilled.select(item.icon) && (
          <span className="bg-primary/8 text-primary flex size-12 shrink-0 items-center justify-center rounded-xl">
            <IconComponent icon={item.icon} size="medium" className="!size-6" />
          </span>
        )}

        <div>
          {isFilled.keyText(item.top_title) && (
            <h5 className="font-rubik text-primary w-full justify-start text-[15px] leading-6 font-medium tracking-tight uppercase md:tracking-tight">
              {item.top_title}
            </h5>
          )}

          <RichText
            field={item.title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-[22px] leading-7 font-medium tracking-tight">{children}</h2>
              ),
              heading3: ({ children }) => (
                <h3 className="text-[22px] leading-7 font-medium tracking-tight">{children}</h3>
              ),
              heading4: ({ children }) => (
                <h4 className="text-[22px] leading-7 font-medium tracking-tight">{children}</h4>
              ),
              heading5: ({ children }) => (
                <h5 className="text-[22px] leading-7 font-medium tracking-tight">{children}</h5>
              ),
              heading6: ({ children }) => (
                <h6 className="text-[22px] leading-7 font-medium tracking-tight">{children}</h6>
              ),
            }}
          />
        </div>
      </div>
    ) : (
      <div className="flex flex-col items-start justify-start">
        {isFilled.keyText(item.top_title) && (
          <h5 className="font-rubik text-primary w-full justify-start text-[15px] leading-6 font-medium tracking-tight uppercase md:tracking-tight">
            {item.top_title}
          </h5>
        )}

        <RichText
          field={item.title}
          components={{
            heading2: ({ children }) => (
              <h2 className="text-[22px] font-medium tracking-tight">{children}</h2>
            ),
            heading3: ({ children }) => (
              <h3 className="text-[22px] font-medium tracking-tight">{children}</h3>
            ),
            heading4: ({ children }) => (
              <h4 className="text-[22px] font-medium tracking-tight">{children}</h4>
            ),
            heading5: ({ children }) => (
              <h5 className="text-[22px] font-medium tracking-tight">{children}</h5>
            ),
            heading6: ({ children }) => (
              <h6 className="text-[22px] font-medium tracking-tight">{children}</h6>
            ),
          }}
        />
      </div>
    )}

    <div className="mt-2 flex w-full flex-col items-start justify-start gap-2">
      <RichText
        field={item.text}
        components={{
          paragraph: ({ children }) => <p className="text-[17px]">{children}</p>,
          list: ({ children }) => <ul className="mt-5 list-none space-y-2 text-left">{children}</ul>,
          listItem: ({ children }) => (
            <li className="flex items-start gap-2 text-left">
              <IconComponent icon="ChevronRight" size="xsmall" className="text-primary mt-1.5 !size-3" />
              {children}
            </li>
          ),
        }}
      />
    </div>

    {isFilled.link(item.link) && item.link.variant === 'Overlay' ? (
      <ContentLink field={item.link} linkResolver={linkResolver} className="absolute inset-0 z-10" />
    ) : (
      <div className="">
        {/* TODO: Implement other link variants */}
        <ContentLink
          field={item.link}
          linkResolver={linkResolver}
          className="hover:text-primary text-base font-semibold"
        />
      </div>
    )}
  </div>
)
export default FeatureHighlights
